import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  questionnaireSchema,
  type QuestionnaireForm,
} from "../schemas/questionnaireSchema";
import type { Answers, PlansResponse, SelfServeProduct } from "../wizardTypes";
import { bookMeetingUrl } from "../wizardTypes";
import { emailDomain } from "../useWizardAnalytics";
import { mixpanelPlgTrack } from "src/services/mixpanelService";
import styles from "../css/SignupWizard.module.css";

type VolumeOption = {
  value: string;
  label: string;
  enterprise: boolean;
};

/**
 * Builds the volume dropdown options from the plans API response.
 * Falls back to an empty list if the product isn't found.
 */
function buildVolumeOptions(
  plans: PlansResponse | null,
  productKey: string,
): VolumeOption[] {
  const product = plans?.products.find((p) => p.productKey === productKey);
  if (!product) return [];
  const unitLabel = product.unit.split(" / ")[1] ?? "month";
  const lastTier = product.tiers[product.tiers.length - 1];
  return [
    { value: "", label: "Select volume\u2026", enterprise: false },
    ...product.tiers
      .filter((t) => !t.isEnterprise)
      .map((t) => ({
        value: String(t.includedQty),
        label: `Up to ${t.includedQty.toLocaleString()} / ${unitLabel}`,
        enterprise: false,
      })),
    ...(lastTier
      ? [
          {
            value: "enterprise",
            label: `More than ${lastTier.includedQty.toLocaleString()} / ${unitLabel}`,
            enterprise: true,
          },
        ]
      : []),
    { value: "unknown", label: "Not sure yet", enterprise: false },
  ];
}

type Props = {
  onSubmit: (answers: Answers) => void;
  onToSales: (reason: string, email?: string, navigate?: boolean) => void;
  defaultValues?: Partial<QuestionnaireForm>;
  products: SelfServeProduct[];
  plans: PlansResponse | null;
  loading?: boolean;
};

export function QuestionnaireStep({
  onSubmit,
  onToSales,
  defaultValues,
  products,
  plans,
  loading,
}: Props) {
  const {
    control,
    handleSubmit,
    watch,
    register,
    setValue,
    formState: { errors, isValid },
  } = useForm<QuestionnaireForm>({
    resolver: zodResolver(questionnaireSchema),
    mode: "onChange",
    defaultValues: {
      needs: ["dncscrub"],
      ...defaultValues,
    },
  });

  // One event per answer change so Flows shows fill order and the last field
  // touched before an abandon. Email is skipped: it changes per keystroke and
  // the address itself never goes to analytics.
  useEffect(() => {
    const sub = watch((values, { name }) => {
      if (!name || name === "email") return;
      mixpanelPlgTrack("questionnaire_changed", {
        field: name,
        value: values[name] || undefined,
        needs: values.needs,
      });
    });
    return () => sub.unsubscribe();
  }, [watch]);

  const needs = watch("needs") ?? [];
  const scrubVolume = watch("scrubVolume");
  const callerIdVolume = watch("callerIdVolume");
  const needsScrub =
    needs.includes("dncscrub") || needs.includes("reassigned");
  const needsCallerId = needs.includes("trustcall");

  // Build dynamic volume options from API response
  const scrubVolumeOptions = buildVolumeOptions(plans, "dncscrub");
  const callerIdVolumeOptions = buildVolumeOptions(plans, "trustcall");

  // Check if an enterprise volume is selected
  const isEnterprise =
    scrubVolumeOptions.find((o) => o.value === scrubVolume)?.enterprise ||
    callerIdVolumeOptions.find((o) => o.value === callerIdVolume)?.enterprise;

  // "Talk to our team first" is the exclusive checkbox
  const isExclusive = (needs as string[]).includes("unsure");
  const toSales = isExclusive || isEnterprise;

  const handleFormSubmit = (data: QuestionnaireForm) => {
    // isExclusive wins when both are true — someone who ticked "unsure" told
    // us directly what they are, regardless of what volume they also picked.
    const salesReason = isExclusive
      ? "talk_to_team"
      : isEnterprise
        ? "enterprise_volume"
        : undefined;

    // Fired before the sales/plan split so every submitted answer set is
    // recorded, including the leads that never reach signup_started.
    // Flat props so each can be a Mixpanel breakdown; volume props are
    // omitted (not "") when that question was not shown.
    mixpanelPlgTrack("questionnaire_submitted", {
      needs: data.needs,
      needs_count: data.needs.length,
      scrub_volume: data.scrubVolume || undefined,
      caller_id_volume: data.callerIdVolume || undefined,
      route: salesReason ? "sales" : "plan",
      reason: salesReason,
      email_domain: emailDomain(data.email),
    });

    if (salesReason) {
      onToSales(salesReason, data.email || undefined);
      return;
    }
    const answers: Answers = {
      needs: data.needs,
      volumes: {},
      email: data.email,
    };
    if (data.scrubVolume) answers.volumes.scrubVolume = data.scrubVolume;
    if (data.callerIdVolume)
      answers.volumes.callerIdVolume = data.callerIdVolume;
    onSubmit(answers);
  };

  // The schema already relaxes the rules on the talk-to-our-team path (no volumes, email
  // optional), so validity is the single source of truth for enabling the button.
  const canSubmit = isValid && !loading;

  return (
    <div className={styles.stepSingle}>
      <p className={styles.stepHeaderLabel}>
        Sign Up
      </p>
      <h2 className={styles.pageTitle}>
        Ready to Stop Your Next TCPA Lawsuit and Improve Call Deliverability?
      </h2>

      <p className={styles.statsLine}>
        <strong>20+ Years</strong> &middot;{" "}
        <strong>140 Billion</strong> Numbers Scrubbed &middot;{" "}
        <strong>500 Thousand</strong> Caller IDs Remediated &middot;{" "}
        <strong>Zero</strong> Client Fines
      </p>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>
            What&rsquo;s putting you at risk right now?
            <span className={styles.legendHint}>Select all that apply</span>
          </legend>

          {/* Needs multi-select */}
          <div className={styles.checkboxGroup}>
            <Controller
              name="needs"
              control={control}
              render={({ field }) => (
                <>
                  {products.map((product) => {
                    const checked =
                      (field.value as string[])?.includes(product.productKey) ?? false;
                    return (
                      <label
                        key={product.productKey}
                        className={`${styles.checkboxLabel} ${checked ? styles.checkboxLabelChecked : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            if (checked) {
                              field.onChange(
                                field.value.filter(
                                  (v: string) => v !== product.productKey,
                                ),
                              );
                            } else {
                              // Clear "unsure" (exclusive) when picking a product
                              const filtered = (field.value ?? []).filter(
                                (v: string) => v !== "unsure",
                              );
                              field.onChange([...filtered, product.productKey]);
                            }
                          }}
                        />
                        <span>
                          <span className={styles.checkboxTitle}>
                            {product.name}
                          </span>
                          <span className={styles.checkboxDesc}>
                            {product.description}
                          </span>
                        </span>
                      </label>
                    );
                  })}

                  {/* "Talk to our team first" — exclusive */}
                  {(() => {
                    const checked = (field.value as string[])?.includes("unsure") ?? false;
                    return (
                      <label
                        className={`${styles.checkboxLabel} ${checked ? styles.checkboxLabelChecked : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            if (checked) {
                              field.onChange(
                                field.value.filter(
                                  (v: string) => v !== "unsure",
                                ),
                              );
                            } else {
                              // Exclusive: clear all others
                              field.onChange(["unsure"]);
                              // Clear volume selections
                              setValue("scrubVolume", "");
                              setValue("callerIdVolume", "");
                            }
                          }}
                        />
                        <span>
                          <span className={styles.checkboxTitle}>
                            Talk to our team first
                          </span>
                          <span className={styles.checkboxDesc}>
                            Not sure where to start, or you need enterprise
                            volume, an MSA or a security review.
                          </span>
                        </span>
                      </label>
                    );
                  })()}
                </>
              )}
            />
          </div>
          {errors.needs && (
            <p className={styles.errorText}>{errors.needs.message}</p>
          )}
        </fieldset>

        <div className={styles.fieldsWrap}>
          {/* Email */}
          <label className={styles.fieldGroup}>
            <span className={styles.label}>
              Business email{!isExclusive && <span aria-hidden="true">*</span>}
            </span>
            <input
              type="email"
              className={`${styles.input} ${errors.email ? styles.inputError : ""}`}
              placeholder="you@company.com"
              autoComplete="email"
              {...register("email")}
            />
            {errors.email && (
              <p className={styles.errorText}>{errors.email.message}</p>
            )}
            {isExclusive && !errors.email && (
              <p className={styles.sidebarFine}>
                Optional &mdash; we&rsquo;ll pre-fill it on the booking page.
              </p>
            )}
          </label>

          {/* Scrub volume — dropdown */}
          {needsScrub && (
            <label className={styles.fieldGroup}>
              <span className={styles.label}>
                Monthly scrub volume<span aria-hidden="true">*</span>
                <span className={styles.labelHint}>
                  Numbers or lookups per month
                </span>
              </span>
              <select
                className={styles.input}
                {...register("scrubVolume")}
              >
                {scrubVolumeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.scrubVolume && (
                <p className={styles.errorText}>{errors.scrubVolume.message}</p>
              )}
            </label>
          )}

          {/* Caller ID volume — dropdown */}
          {needsCallerId && (
            <label className={styles.fieldGroup}>
              <span className={styles.label}>
                Caller ID volume<span aria-hidden="true">*</span>
                <span className={styles.labelHint}>
                  Outbound phone numbers you own
                </span>
              </span>
              <select
                className={styles.input}
                {...register("callerIdVolume")}
              >
                {callerIdVolumeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.callerIdVolume && (
                <p className={styles.errorText}>
                  {errors.callerIdVolume.message}
                </p>
              )}
            </label>
          )}
        </div>

        {/* Enterprise note */}
        {isEnterprise && (
          <p className={styles.enterpriseNote}>
            At this volume we&rsquo;ll set you up directly rather than through
            a checkout page.
          </p>
        )}

        <button
          type="submit"
          className={styles.btnPrimary}
          disabled={!canSubmit}
        >
          {loading
            ? "Building your plan\u2026"
            : toSales
              ? "Talk to our team"
              : "Get started"}
        </button>

        <p className={styles.sidebarFine} style={{ marginTop: "1rem" }}>
          Already have an account?{" "}
          <a href="https://www.dncscrub.com/" target="_top">Log in</a> &middot; Prefer to
          talk?{" "}
          <a
            href={bookMeetingUrl(watch("email"))}
            target="_top"
            rel="noopener noreferrer"
            onClick={() => onToSales("questionnaire_link", watch("email"), false)}
          >
            Talk to our compliance team
          </a>
        </p>
      </form>
    </div>
  );
}
